import { Injectable, inject } from '@angular/core';
import { Coding } from 'fhir/r4';
import { terminologyCanonicalUrlBaseToken } from '../app.config';
import { AuthService, User } from './auth/auth.service';

export type TenantTaggableResource = {
    meta?: {
        tag?: Coding[];
        [key: string]: any;
    };
    [key: string]: any;
} | null | undefined;

@Injectable({ providedIn: 'root' })
export class TenantMetadataService {
    private authService = inject(AuthService);
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly organizationTenantSystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;

    ensureTenantTag(resource: TenantTaggableResource, organizationId?: string, organizationDisplayName?: string): string {
        if (!resource || !organizationId) {
            return organizationDisplayName?.trim() || '';
        }

        resource.meta = resource.meta ?? {};
        const tags: Coding[] = Array.isArray(resource.meta.tag)
            ? resource.meta.tag
            : (resource.meta.tag = []);

        const resolvedDisplay = this.resolveDisplayName(organizationDisplayName);
        const existingTag = tags.find(tag => tag?.system === this.organizationTenantSystem && tag?.code === organizationId);

        if (existingTag) {
            if (resolvedDisplay && !existingTag.display) {
                existingTag.display = resolvedDisplay;
            }
            return existingTag.display || resolvedDisplay || '';
        }

        const coding: Coding = {
            system: this.organizationTenantSystem,
            code: organizationId
        };

        if (resolvedDisplay) {
            coding.display = resolvedDisplay;
        }

        tags.push(coding);
        return resolvedDisplay || '';
    }

    private resolveDisplayName(explicit?: string): string {
        if (explicit?.trim()) {
            return explicit.trim();
        }

        const fromUser = this.resolveOrganizationDisplayFromUser(this.authService.user.getValue());
        if (fromUser) {
            return fromUser;
        }

        const fromStorage = this.readOrganizationNameFromStorage();
        if (fromStorage) {
            return fromStorage;
        }

        return '';
    }

    private resolveOrganizationDisplayFromUser(user: User | null | undefined): string {
        if (!user) {
            return '';
        }

        const candidates: Array<string | undefined> = [
            (user as any)?.organizationName,
            (user as any)?.organization?.name,
            (user as any)?.organization?.display,
            (user as any)?.organizationDisplay,
            (user as any)?.hospitalName,
            (user as any)?.hospital?.name,
            (user as any)?.hospital?.display,
            (user as any)?.hospitalDisplay
        ];

        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }

        return '';
    }

    private readOrganizationNameFromStorage(): string {
        const storageKeys = ['selectedOrganizationName', 'selectedHospitalName', 'hospitalName'];

        for (const key of storageKeys) {
            const sessionValue = this.safeGet(sessionStorage, key);
            if (sessionValue) {
                return sessionValue;
            }

            const localValue = this.safeGet(localStorage, key);
            if (localValue) {
                return localValue;
            }
        }

        return '';
    }

    private safeGet(storage: Storage, key: string): string {
        try {
            const value = storage.getItem(key);
            return value?.trim() || '';
        } catch {
            return '';
        }
    }
}
